// Company communication directory — the reformatted content of the official
// "riferimenti aziendali" email. NOT the org chart: it maps activities to the
// right contact/mailbox. General Management (rscarano@ticketstation.it) must be
// cc'd on the communications flagged below.

export const CC_GENERAL_MANAGEMENT = 'rscarano@ticketstation.it';

export interface Office {
  name: string;
  address: string;
  email?: string;
  mobile?: string;
  phone?: string;
  note?: string;
}

export interface RoleContact {
  role: string;
  person?: string;
  email?: string;
  competencies?: string[];
  ccGm?: boolean; // General Management always in copy
  note?: string;
}

export interface DirectorySection {
  title: string;
  icon: string;
  roles: RoleContact[];
}

export const COMPANY_INTRO = {
  scope:
    'Al fine di garantire una comunicazione efficace e tempi di risposta rapidi, tutto il personale è tenuto a utilizzare i riferimenti indicati in base alla tipologia della richiesta. Questo documento individua i contatti aziendali e le figure di riferimento — non rappresenta l’organigramma ufficiale.',
  company:
    'Ticketstation S.r.l. (brand Touristation) è un Tour Operator/Agenzia di Viaggi che opera in contratto di rete con Visit Rome S.r.l. Siti: touristation.it e visitrome.info.',
};

export const OFFICES: Office[] = [
  { name: 'Touristation Aracoeli', address: "Piazza d'Aracoeli, 16 – Roma", email: 'colosseo@ticketstation.it', mobile: '+39 349 696 6958', phone: '+39 06 69942315' },
  { name: 'Touristation Vaticano', address: 'Viale Vaticano, 97 – Roma', email: 'vaticano@ticketstation.it', mobile: '+39 347 974 5405', phone: '+39 06 6830093' },
  { name: 'Touristation Musei Vaticani', address: 'Viale Vaticano, 101 – Roma', email: 'musei@ticketstation.it', phone: '+39 06 87669630' },
  { name: 'Touristation Cappella Sistina', address: 'Viale Vaticano, 95 – Roma', email: 'sanpietro@ticketstation.it', mobile: '+39 349 972 7069', phone: '+39 06 39031108' },
  { name: 'Touristation Navona', address: 'Piazza Navona, 25 – Roma', email: 'navona@ticketstation.it', mobile: '+39 329 764 6595', phone: '+39 06 83957653' },
  { name: 'Touristation Borgo Vittorio', address: 'Apertura prossima', note: 'Apertura prossima' },
  { name: 'Back Office · Amministrazione & Coordinamento', address: 'Via Orazio 31 – Scala A – 5° piano', phone: '+39 06 69281645' },
];

export const DIRECTORY: DirectorySection[] = [
  {
    title: 'Direzione e coordinamento',
    icon: 'star',
    roles: [
      { role: 'General Manager', person: 'Raffaella Scarano', email: 'rscarano@ticketstation.it',
        note: 'Da inserire sempre in copia nelle comunicazioni di particolare rilevanza; coinvolta in: criticità operative, decisioni strategiche, problematiche del personale, rapporti con partner/enti/fornitori rilevanti, nuove opportunità commerciali, proposte di collaborazione e sviluppo.' },
      { role: 'Coordinamento Generale', person: 'Olga Chakvina', email: 'coordinator@ticketstation.it', ccGm: true,
        competencies: ['Supporto al General Manager', 'Coordinamento generale delle attività', 'Supporto al coordinamento operativo', 'Supervisione prodotti', 'Supervisione attività commerciali', 'Supporto trasversale a punti vendita e reparti', 'Reportistica staff'] },
      { role: 'Coordinamento Commerciale', person: 'Sara Broglio', ccGm: true,
        competencies: ['Reparto Prodotto', 'Rapporti commerciali con OTA e partner', 'Coordinamento commerciale dei prodotti', 'Supporto alla distribuzione', 'Analisi e sviluppo nuove opportunità commerciali'] },
      { role: 'Coordinamento Operativo', person: 'Adalgisa Menichelli', email: 'sales@ticketstation.it', ccGm: true,
        competencies: ['Coordinamento operativo quotidiano', 'Supporto operativo ai punti vendita', 'Gestione problematiche tecniche e operative', 'Reportistica operativa', 'Gestione sistemi aziendali (STEP, Bokun, API)'] },
    ],
  },
  {
    title: 'Commerciale, marketing e operations',
    icon: 'chart',
    roles: [
      { role: 'Marketing & Business Development', person: 'Simona Pignotti', email: 'marketing@ticketstation.it', ccGm: true,
        competencies: ['Marketing e comunicazione', 'Business Development', 'Rapporti con enti e istituzioni', 'Gestione contratti', 'Gestione gruppi incoming', 'Supporto Coordinamento generale'] },
      { role: 'Operations B2B – Touristation', person: 'Jessica Aversa', email: 'operation@ticketstation.it',
        competencies: ['Coordinamento operativo attività B2B', 'Gestione prenotazioni B2B', 'Gestione richieste operative e criticità'] },
      { role: 'Customer Service B2C – Touristation', person: 'Simona Pignotti', email: 'info@touristation.it',
        competencies: ['Assistenza clienti canale B2C', 'Gruppi incoming e servizi privati dal sito Touristation', 'Informazioni su prodotti e servizi', 'Modifiche prenotazioni'] },
    ],
  },
  {
    title: 'Turni, HR e IT',
    icon: 'users',
    roles: [
      { role: 'Turni di lavoro', email: 'roster@ticketstation.it', ccGm: true,
        note: 'Solo per: richieste ferie, richieste off, cambi turno, disponibilità lavorativa e ogni comunicazione sui turni.' },
      { role: 'Risorse Umane – HR', person: 'Laura Salvatelli', email: 'hr@ticketstation.it', ccGm: true,
        competencies: ['Contratti di lavoro', 'Buste paga', 'Comunicazioni di malattia', 'Pratiche amministrative del personale'] },
      { role: 'Dipartimento IT', person: 'Riccardo Pambianco', email: 'techsupport@ticketstation.it', ccGm: true,
        competencies: ['Automazioni e workflow digitali', 'Soluzioni automatizzate a supporto delle attività', 'Integrazioni tra sistemi, API e piattaforme', 'Digitalizzazione e miglioramento processi', 'Soluzioni tecnologiche innovative'] },
    ],
  },
  {
    title: 'Amministrazione, contabilità e logistica',
    icon: 'lock',
    roles: [
      { role: 'Amministrazione e Contabilità', person: 'Michele Meletti', email: 'amministrazione@ticketstation.it',
        competencies: ['Sales report, chiusure di cassa e POS', 'Fatturazione', 'Rapporti amministrativi con fornitori', 'Pagamenti', 'Attività amministrative ordinarie', 'Supporto amministrativo alla logistica'] },
      { role: 'Contabilità / Finance', person: 'Nicoletta Sciarra', email: 'finance@ticketstation.it',
        competencies: ['Contabilità aziendale', 'Controllo di gestione', 'Reporting finanziario'],
        note: 'Solo attività contabili e finanziarie — non è un riferimento operativo per il personale.' },
      { role: 'Logistica', person: 'Fabrizio Livata', email: 'logistica@ticketstation.it',
        competencies: ['Manutenzioni', 'Coordinamento pulizie', 'Forniture e reintegro materiali', 'Supporto logistico ai punti vendita'],
        note: 'Per acquisti/forniture/pagamenti inserire sempre in copia amministrazione@ticketstation.it e rscarano@ticketstation.it.' },
    ],
  },
];

export const OPERATING_GUIDELINES = [
  'Usa sempre l’indirizzo e-mail corretto in base alla tipologia della richiesta.',
  'Evita di indirizzare comunicazioni a figure non competenti.',
  'Inserisci in copia i referenti indicati, quando previsto.',
  'Riporta tutte le informazioni necessarie per una gestione corretta e tempestiva.',
  'In caso di dubbi sull’interlocutore, rivolgiti al tuo responsabile o al Coordinamento Generale.',
];
